import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import dayjs from "dayjs";
import useApi from "./useApi";
import { useEffect, useState } from "react";
import { useAuth } from "../context/authContext";
import { ACHIEVEMENTS_KEY, invalidateListCaches } from "../utils/cacheUtils";
import { handleApiError, safeMutate } from "../utils/utils";
import { fetchStatusBatched } from "../utils/statusBatcher";
import { fetchJournalBatched } from "../utils/journalBatcher";
import { toast } from "react-toastify";

/**
 * useUnreadNotificationCount
 *
 * Returns the unread count for a given notification kind. The what's-new dot
 * uses this with kind="whats_new" and treats count > 0 as a boolean. The same
 * hook can be reused for the future personal-notification bell with a different kind.
 */
export function useUnreadNotificationCount(kind = "whats_new") {
  const { getUnreadNotificationCount } = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: ["notifications", "unreadCount", kind],
    queryFn: () => getUnreadNotificationCount(kind),
    enabled: !!user,
    // Override global defaults so the badge refreshes when an installed PWA returns
    // from background (React tree stays mounted, so refetchOnMount doesn't fire).
    // staleTime: 0 ensures data is always considered stale, which is required for
    // refetchOnWindowFocus to actually trigger — React Query skips the refetch if
    // data is still fresh.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/**
 * useAdminMetrics
 *
 * Live metrics for the admin dashboard. Recomputed server-side on every call,
 * so a short staleTime keeps a refocus from re-running the aggregate queries
 * while still letting a manual refetch through.
 */
export function useAdminMetrics() {
  const { fetchAdminMetrics } = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: ["admin", "metrics"],
    queryFn: fetchAdminMetrics,
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * useAdminMetricsHistory
 *
 * Nightly snapshots. Only these give an accurate long-run trend — the live
 * endpoint can only ever describe the present.
 */
export function useAdminMetricsHistory(days = 180) {
  const { fetchAdminMetricsHistory } = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: ["admin", "metricsHistory", days],
    queryFn: () => fetchAdminMetricsHistory(days),
    enabled: !!user,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
}

/**
 * useAdminBookCovers
 *
 * Books matching a title or content id, each with every edition cover. Only
 * runs once a query is submitted.
 */
export function useAdminBookCovers(query) {
  const { fetchAdminBookCovers } = useApi();
  const { user } = useAuth();
  return useQuery({
    queryKey: ["admin", "bookCovers", query],
    queryFn: () => fetchAdminBookCovers(query),
    enabled: !!user && !!query,
    retry: false,
  });
}

/**
 * useSetAdminBookCover
 *
 * Swaps a book's primary cover. Content metadata is invalidated so the new
 * cover shows elsewhere in this session without a reload.
 */
export function useSetAdminBookCover() {
  const { setAdminBookCover } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setAdminBookCover,
    onSuccess: (_data, { contentId }) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "bookCovers"] });
      queryClient.invalidateQueries({ queryKey: ["book", "metadata", contentId] });
      toast.success("Cover updated");
    },
    onError: (error) => handleApiError(error),
  });
}

/**
 * useRefetchBookEditions
 *
 * Re-runs a book's ISBNdb edition fetch (synchronous server-side, a few seconds)
 * and reloads the cover picker so any new editions show.
 */
export function useRefetchBookEditions() {
  const { refetchAdminBookEditions } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: refetchAdminBookEditions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "bookCovers"] });
      toast.success("Editions refetched");
    },
    onError: (error) => handleApiError(error),
  });
}

/**
 * useContentMetadata
 *
 * Fetches and caches metadata for a piece of content, with special handling
 * for TV shows to ensure that ALL TV metadata is canonicalised and cached
 * at the SERIES level, even when the user navigates via an individual season.
 *
 * ---- HOW IT WORKS -------------------------------------------------------
 *
 * 1. A manual lookup map is stored in the React Query cache under the key:
 *
 *      ["getSeriesContentIdFromTVShow"]
 *
 *    This map has the shape:
 *
 *      {
 *        [seasonContentId]: seriesContentId
 *      }
 *
 *    IMPORTANT:
 *    - This is NOT a real query
 *    - It is written manually via `setQueryData`
 *    - It will NOT appear in React Query DevTools
 *
 * 2. When this hook is called for `contentType === "tvshow"`:
 *    - If the provided contentId is a SEASON and we already know its
 *      corresponding series ID, we transparently swap the ID
 *    - The metadata query is then executed using the SERIES ID instead
 *
 * 3. Once series metadata is successfully fetched:
 *    - We iterate over `data.seasons`
 *    - We populate / update the lookup map so that each season ID
 *      points to the parent series ID
 *
 * 4. From this point on:
 *    - Any request for metadata for ANY season of this series
 *      will be resolved from the SAME cached series-level query
 *
 * ---- IMPORTANT CONSEQUENCES ---------------------------------------------
 *
 * - There is exactly ONE metadata cache entry per TV series
 * - Season navigation is instant once metadata has loaded
 * - Other parts of the app can reliably infer:
 *     - "Is this content a season?" by checking lookup[contentId]
 *     - "Is this content a series?" by absence from the lookup
 */
export function useContentMetadata(contentType, contentId) {
  const queryClient = useQueryClient();
  const { fetchContentMetadata } = useApi();
  const lookup =
    queryClient.getQueryData(["getSeriesContentIdFromTVShow"]) ?? {};
  // This is "manual" (i.e. non-query) cache data and so will not appear in the react query dev tools

  const effectiveId =
    contentType === "tvshow" && lookup[contentId]
      ? lookup[contentId]
      : contentId;
  // This ensures tvshow metadata is read from the cache if series data is already cached

  const queryResult = useQuery({
    queryKey: [contentType, "metadata", effectiveId],
    queryFn: () => fetchContentMetadata(contentType, effectiveId),
    enabled: !!contentType && !!effectiveId,
    placeholderData: (prevData) => prevData,
  });

  useEffect(() => {
    if (contentType !== "tvshow") return;

    const data = queryResult.data;
    if (!data) return;

    const seasons = data.seasons;
    if (!seasons || seasons.length === 0) return;

    const seriesId = seasons[0].content_id;

    // Read latest lookup inside the effect to avoid stale values
    const currentLookup =
      queryClient.getQueryData(["getSeriesContentIdFromTVShow"]) ?? {};

    const newEntries = {};
    seasons.forEach((s) => {
      // Creates mapping where season id is the key and series id is the value
      newEntries[s.content_id] = seriesId;
    });

    const updated = { ...currentLookup, ...newEntries };

    // Write back only if there was a real change
    if (JSON.stringify(updated) !== JSON.stringify(currentLookup)) {
      queryClient.setQueryData(["getSeriesContentIdFromTVShow"], updated);
    }
  }, [queryResult.data, queryClient, contentType]);

  return queryResult;
}

// Shared by useContentStatus and usePrefetchContentStatuses so a tile's own
// fetch and a parent's prefetch dedupe onto the same query (and, via
// fetchStatusBatched, the same outgoing request) regardless of which fires first.
function contentStatusQueryOptions(contentId, fetchBatchStatuses) {
  return {
    queryKey: ["contentStatus", contentId],
    queryFn: () => fetchStatusBatched(contentId, fetchBatchStatuses),
  };
}

export function useContentStatus(contentId, options = {}) {
  const { enabled = true } = options;
  const { fetchBatchStatuses } = useApi();

  return useQuery({
    ...contentStatusQueryOptions(contentId, fetchBatchStatuses),
    enabled: enabled && !!contentId,
    placeholderData: (prevData) => prevData,
  });
}

// Season count is dynamic, so a fixed number of useContentStatus calls won't
// work — useQueries lets the list of queries itself vary between renders.
// Each query still resolves through fetchStatusBatched, so all season ids
// (plus the series id, if included) coalesce into a single /statuses/batch
// request and dedupe onto the same ["contentStatus", id] cache entries the
// status button and PosterTile already read.
export function useContentStatuses(contentIds, options = {}) {
  const { enabled = true } = options;
  const { fetchBatchStatuses } = useApi();

  const results = useQueries({
    queries: (contentIds ?? []).map((id) => ({
      ...contentStatusQueryOptions(id, fetchBatchStatuses),
      enabled: enabled && !!id,
      placeholderData: (prevData) => prevData,
    })),
  });

  return (contentIds ?? []).reduce((acc, id, index) => {
    acc[id] = results[index]?.data ?? "none";
    return acc;
  }, {});
}

// The Status enum in the backend's models.py, in declaration order. Postgres
// sorts enum columns by that order, so this is what Journal.status.desc()
// resolves to — later entries here sort first.
const STATUS_ORDER = [
  "queued",
  "started",
  "abandoned",
  "finished",
  "logged", // not currently in use, kept so the order matches the enum
];

/**
 * Single-content view (season/movie/book journal) mirrors the backend
 * ordering in get_journal_for_content: date desc (nulls last), then
 * updated_at desc, then status desc.
 *
 * `seasonNumberById` is supplied only for the merged TV series list. There,
 * season outranks status: higher seasons sort above lower ones and the series
 * itself (season 0) above all of them, since on a whole-series view the season
 * is the more meaningful distinction between two entries logged the same day.
 * updated_at drops down to a final tiebreak after status, rather than sitting
 * ahead of season — it otherwise decides same-day ties before season gets a
 * chance to, since two entries almost never share an updated_at exactly.
 *
 * Imported entries routinely share both a date and an updated_at, so the
 * status tiebreaker is load-bearing rather than theoretical — without it, tied
 * entries fall back to whatever order the queries happened to resolve in.
 */
function makeJournalComparator(seasonNumberById) {
  // Season 0 is the synthetic "series itself" entry, which belongs at the very
  // top rather than beneath season 1.
  const seasonRank = (entry) => {
    const seasonNumber = seasonNumberById?.[entry.content_id];
    if (seasonNumber == null) return -Infinity;
    return seasonNumber === 0 ? Infinity : seasonNumber;
  };

  return (a, b) => {
    // date desc, undated last
    if (a.date == null && b.date != null) return 1;
    if (b.date == null && a.date != null) return -1;
    if (a.date != null && b.date != null && a.date !== b.date) {
      return b.date.localeCompare(a.date);
    }

    if (seasonNumberById) {
      // Compared rather than subtracted: the ranks include Infinity, and
      // Infinity - Infinity is NaN, which would corrupt the sort.
      const rankA = seasonRank(a);
      const rankB = seasonRank(b);
      if (rankA !== rankB) return rankB > rankA ? 1 : -1;

      if (a.status !== b.status) {
        return STATUS_ORDER.indexOf(b.status) - STATUS_ORDER.indexOf(a.status);
      }
      return b.updated_at.localeCompare(a.updated_at);
    }

    if (a.updated_at !== b.updated_at) {
      return b.updated_at.localeCompare(a.updated_at);
    }
    return STATUS_ORDER.indexOf(b.status) - STATUS_ORDER.indexOf(a.status);
  };
}

// Same dynamic-length-of-ids rationale as useContentStatuses: a TV series'
// season count varies, so the list of queries must vary too. Each query keeps
// the SAME ["journal", id] key that fetchJournalForContent's single-id query
// and all of cacheUtils.js's mutation helpers already use, so a series view
// (many ids) and a season view (one id) always read/write the same cache
// entries and can never drift apart. Entries fetched here coalesce onto a
// single /journalforcontent/batch request via fetchJournalBatched.
export function useJournalForContents(contentIds, options = {}) {
  const { enabled = true, seasonNumberById = null } = options;
  const { fetchBatchJournalForContent } = useApi();

  const results = useQueries({
    queries: (contentIds ?? []).map((id) => ({
      queryKey: ["journal", id],
      queryFn: () => fetchJournalBatched(id, fetchBatchJournalForContent),
      enabled: enabled && !!id,
      placeholderData: (prevData) => prevData,
    })),
  });

  const entries = results
    .flatMap((result) => result.data ?? [])
    .sort(makeJournalComparator(seasonNumberById));

  return {
    entries,
    isLoading: results.some((result) => result.isLoading),
  };
}

export function useBookMetadata(olWid) {
  const { fetchBookMetaData } = useApi();

  return useQuery({
    queryKey: ["book", "metaData", olWid],
    queryFn: () => fetchBookMetaData(olWid),
    enabled: !!olWid,
  });
}

/**
 * Fetches (in one batch) the statuses of any `contentIds` not yet cached.
 * isLoading is true from the very first render whenever some are missing —
 * not only once the effect starts the fetch — so a consumer such as
 * useStatusSummary never counts against a half-empty cache in between (a list
 * opened by URL would otherwise read 0% finished, then jump to its real value).
 */
export function usePrefetchContentStatuses(contentIds, options = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();
  const { fetchBatchStatuses } = useApi();
  const [isLoading, setIsLoading] = useState(
    () =>
      enabled &&
      !!contentIds?.some(
        (id) => queryClient.getQueryData(["contentStatus", id]) === undefined,
      ),
  );

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const missingIds = (contentIds ?? []).filter(
      (id) => queryClient.getQueryData(["contentStatus", id]) === undefined,
    );
    // Nothing to fetch — also clears the initial loading state if the
    // statuses arrived (e.g. via tiles) between first render and now
    if (!missingIds.length) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    // ensureQueryData runs the same queryFn a tile's useContentStatus would,
    // so a missing id fetched here rides the same batched request as any
    // PosterTile mounting in the same commit (see statusBatcher.js).
    Promise.all(
      missingIds.map((id) =>
        queryClient.ensureQueryData(
          contentStatusQueryOptions(id, fetchBatchStatuses),
        ),
      ),
    )
      // Prefetch is best-effort: the per-item contentStatus queries fetch on
      // demand otherwise. Swallow network blips (so they don't become unhandled
      // rejections) but still surface a genuine server error.
      .catch((error) => {
        if (error.response) handleApiError(error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, contentIds, queryClient, fetchBatchStatuses]);

  return { isLoading: enabled ? isLoading : false };
}

export function useStatusSummary(contentIds) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const enabled = !!user;

  const { isLoading: prefetching } = usePrefetchContentStatuses(contentIds, {
    enabled,
  });
  const [finishedCount, setFinishedCount] = useState(0);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setFinishedCount(0);
      setIsReady(false);
      return;
    }
    if (prefetching) return;

    const recalc = () => {
      const ids = contentIds ?? [];
      const count = ids.reduce((acc, id) => {
        return queryClient.getQueryData(["contentStatus", id]) === "finished"
          ? acc + 1
          : acc;
      }, 0);
      setFinishedCount(count);
      setIsReady(true);
    };

    recalc(); // initial compute

    const cache = queryClient.getQueryCache();
    const unsubscribe = cache.subscribe((event) => {
      if (
        event?.type === "updated" &&
        event.query.queryKey?.[0] === "contentStatus" &&
        (contentIds ?? []).includes(event.query.queryKey?.[1])
      ) {
        recalc();
      }
    });

    return unsubscribe;
  }, [enabled, prefetching, contentIds, queryClient]);

  const isLoading = enabled ? prefetching || !isReady : false;

  return { finishedCount: enabled ? finishedCount : 0, isLoading };
}

export function useListsForContent(contentId, options = {}) {
  const { fetchListsForContent } = useApi();

  return useQuery({
    queryKey: ["collections", "forContent", contentId],
    queryFn: () => fetchListsForContent(contentId),
    ...options,
  });
}

/**
 * useFollowingForContent
 *
 * Backs the "Following" tab in LibraryModal: statuses + ratings for a content
 * item across the accounts the signed-in user follows.
 */
export function useFollowingForContent(contentId, options = {}) {
  const { enabled = true, ...restOptions } = options;
  const { fetchFollowingForContent } = useApi();

  return useQuery({
    queryKey: ["following", "forContent", contentId],
    queryFn: () => fetchFollowingForContent(contentId),
    enabled: enabled && !!contentId,
    placeholderData: (prevData) => prevData,
    ...restOptions,
  });
}

/**
 * useEntry
 *
 * Resolves a single journal entry for any viewer — backs ?entry=<id> links in
 * LibraryModal, which need to know who owns the entry (to focus someone
 * else's, or scope the You tab to the viewer's own) before rendering it,
 * whether or not the viewer is signed in or follows its author.
 *
 * throwOnError is off so a dead link (e.g. a deleted entry) doesn't take the
 * modal down via the error boundary — the global toast still reports it, and
 * LibraryModal falls back to its normal view.
 */
export function useEntry(journalId, options = {}) {
  const { enabled = true, ...restOptions } = options;
  const { fetchEntry } = useApi();

  return useQuery({
    queryKey: ["entry", journalId],
    queryFn: () => fetchEntry(journalId),
    enabled: enabled && !!journalId,
    throwOnError: false,
    ...restOptions,
  });
}

export function useListMutations({ origin, profileKind, username }) {
  const queryClient = useQueryClient();
  const {
    addItemToList,
    removeItemFromList,
    replaceItemInList,
    reorderItemsInList,
  } = useApi();
  const handleSuccess = (listContentType, listId) => {
    invalidateListCaches({
      queryClient,
      listId,
      origin,
      profileKind,
      username,
      contentType: listContentType,
    });
  };

  const addToList = useMutation({
    mutationFn: ({ listId, contentId }) => addItemToList({ listId, contentId }),
    onSuccess: (data, variables) => {
      handleSuccess(data.list_content_type, variables.listId);
      toast.success(`Added to collection`);
    },
  });

  const removeFromList = useMutation({
    mutationFn: ({ listId, contentId }) =>
      removeItemFromList({ listId, contentId }),
    onSuccess: (data, variables) => {
      handleSuccess(data.list_content_type, variables.listId);
      toast.success(`Removed from collection`);
    },
  });

  const replaceInList = useMutation({
    mutationFn: ({ listId, oldContentId, newContentId }) =>
      replaceItemInList({ listId, oldContentId, newContentId }),
    onSuccess: (data, variables) => {
      handleSuccess(data.list_content_type, variables.listId);
    },
  });

  const reorderList = useMutation({
    mutationFn: ({ listId, items }) => reorderItemsInList({ listId, items }),
    onSuccess: (data, variables) => {
      handleSuccess(data.list_content_type, variables.listId);
    },
  });

  return {
    addToList: safeMutate(addToList.mutateAsync), // toggle in AddContentToCollection needs to await to provide loading state
    removeFromList: safeMutate(removeFromList.mutateAsync), // toggle in AddContentToCollection needs to await to provide loading state
    replaceInList: replaceInList.mutate,
    reorderListAsync: safeMutate(reorderList.mutateAsync), // reordering is debounced which must await as it relies on sequencing of actions
    isAdding: addToList.isPending,
    isRemoving: removeFromList.isPending,
    isReplacing: replaceInList.isPending,
    isReordering: reorderList.isPending,
  };
}

/** The user's local calendar year — what "this year's standouts" means to them. */
export const currentStandoutYear = () => new Date().getFullYear();

/**
 * useStandouts
 *
 * A user's standouts strip for one content type. staleTime 0 because the
 * "from N" count also moves when entries are finished/dated elsewhere in the
 * app, which doesn't invalidate this query. `enabled: false` skips it.
 */
export function useStandouts(username, contentType, { enabled = true } = {}) {
  const { fetchStandouts } = useApi();
  return useQuery({
    queryKey: ["standouts", "summary", username, contentType, currentStandoutYear()],
    queryFn: () => fetchStandouts(contentType, username, currentStandoutYear()),
    enabled: enabled && !!username,
    staleTime: 0,
    // Keep the previous content type's row up while switching, so the page
    // doesn't collapse and lose the scroll position — same user only
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === username ? previousData : undefined,
  });
}

/**
 * useInProgress
 *
 * Your 10 most recently started items, plus the total, for the dashboard's
 * "In progress" row — the first page of the Track page's started tab. Its own
 * key (not infStatuses) since it's a plain query; status writes invalidate it
 * via ["dashboard"] (see cacheUtils). staleTime 0 so it also picks up changes
 * made on another device each time you land on the dashboard.
 */
export function useInProgress(username, contentType) {
  const { fetchStatuses } = useApi();
  return useQuery({
    queryKey: ["dashboard", "inProgress", username, contentType],
    queryFn: () =>
      fetchStatuses({
        perPage: 10,
        username,
        contentType,
        status: "started",
        sortBy: "log_date",
        sortDescending: true,
      }),
    enabled: !!username,
    staleTime: 0,
    // Keep the previous content type's row up while switching (see useStandouts)
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === username ? previousData : undefined,
  });
}

/**
 * useRecentlyFinished
 *
 * The dashboard's "Recently finished" row for one content type. Keyed by the
 * local date, since the current month's comparison is "so far" — a dashboard
 * left open past midnight picks up the new day on its next refetch. staleTime
 * 0 for the same reason as useInProgress.
 */
export function useRecentlyFinished(username, contentType) {
  const { fetchRecentlyFinished } = useApi();
  return useQuery({
    queryKey: [
      "dashboard",
      "recentlyFinished",
      username,
      contentType,
      dayjs().format("YYYY-MM-DD"),
    ],
    queryFn: () => fetchRecentlyFinished(contentType),
    enabled: !!username,
    staleTime: 0,
    // Keep the previous content type's row up while switching (see useStandouts)
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === username ? previousData : undefined,
  });
}

/**
 * useAchievements
 *
 * Your dashboard achievement rows for the selected pill. Status and entry
 * writes refresh it with the rest of ["dashboard"] (see cacheUtils); profile
 * edits, follows, list adds and imports invalidate ACHIEVEMENTS_KEY directly.
 * Your own data only, so not keyed by username.
 */
export function useAchievements(contentType, { enabled = true } = {}) {
  const { fetchAchievements } = useApi();
  return useQuery({
    queryKey: [...ACHIEVEMENTS_KEY, contentType],
    queryFn: () => fetchAchievements(contentType),
    enabled,
    staleTime: 0,
    // Keep the previous pill's rows up while switching (see useStandouts)
    placeholderData: (previousData) => previousData,
    // A quiet inline message (DashboardProgress), not the error boundary
    throwOnError: false,
  });
}

/**
 * Records that achievements' NEW rows have been seen on the dashboard. No
 * cache update: the dot stays for the rest of the visit (DashboardProgress
 * holds it) and is gone from the next fetch. Fire-and-forget — a failure just
 * leaves the dot for next time, so it shouldn't toast.
 */
export function useMarkAchievementsSeen() {
  const { markAchievementsSeen } = useApi();
  return useMutation({
    mutationFn: markAchievementsSeen,
    // Replaces the default toasting handler (defaultOptions in index.js)
    onError: () => {},
  });
}

/** Skips a Getting started step; the response is the refreshed rows for that pill. */
export function useSkipAchievementStep(contentType) {
  const queryClient = useQueryClient();
  const { skipAchievementStep } = useApi();
  return useMutation({
    mutationFn: (stepKey) => skipAchievementStep({ stepKey, contentType }),
    onSuccess: (rows) => {
      queryClient.setQueryData([...ACHIEVEMENTS_KEY, contentType], rows);
      // Other pills' cached rows carry the same profile row
      queryClient.invalidateQueries({ queryKey: ACHIEVEMENTS_KEY });
    },
  });
}

/**
 * useStandoutStatus
 *
 * Standout state for one item (per season for a series). Only enabled while
 * something needs it (the dropdown is open / an add flow is running), and always
 * refetched then, since status/date changes elsewhere affect it.
 */
export function useStandoutStatus(contentId, options = {}) {
  const { enabled = true } = options;
  const { fetchStandoutStatus } = useApi();
  return useQuery({
    queryKey: ["standouts", "status", contentId, currentStandoutYear()],
    queryFn: () => fetchStandoutStatus(contentId, currentStandoutYear()),
    enabled: enabled && !!contentId,
    staleTime: 0,
    // Lives inside menus/dialogs on other pages — a failure should toast (global
    // QueryCache handler), not take the page down via the error boundary
    throwOnError: false,
  });
}

/** Items finished (with a date) this year that aren't standouts yet — profile picker. */
export function useStandoutCandidates(contentType, options = {}) {
  const { enabled = true } = options;
  const { fetchStandoutCandidates } = useApi();
  return useQuery({
    queryKey: ["standouts", "candidates", contentType, currentStandoutYear()],
    queryFn: () => fetchStandoutCandidates(contentType, currentStandoutYear()),
    enabled,
    staleTime: 0,
  });
}

/**
 * useStandoutMutations
 *
 * Add/remove standouts. Adding with a finishDate can mark the item finished and
 * create/date a journal entry (and, for TV, re-derive the series status), so
 * those caches are refreshed too.
 */
export function useStandoutMutations() {
  const queryClient = useQueryClient();
  const { addStandout, removeStandout } = useApi();

  const add = useMutation({
    mutationFn: ({ contentId, finishDate }) =>
      addStandout({ contentId, finishDate, year: currentStandoutYear() }),
    onSuccess: (data, { finishDate }) => {
      queryClient.invalidateQueries({ queryKey: ["standouts"] });
      // Getting started's standout step (and a badge, if this finished the item)
      queryClient.invalidateQueries({ queryKey: ACHIEVEMENTS_KEY });
      // The standouts collection dialog, if open (its list id isn't known here)
      queryClient.invalidateQueries({ queryKey: ["list"] });
      if (finishDate) {
        queryClient.invalidateQueries({ queryKey: ["contentStatus"] });
        queryClient.invalidateQueries({ queryKey: ["journal", data.content_id] });
        queryClient.invalidateQueries({ queryKey: ["infStatuses"] });
        queryClient.invalidateQueries({ queryKey: ["infJournal"] });
        queryClient.invalidateQueries({ queryKey: ["profile"] }); // library counts
      }
      toast.success(`Added to your ${data.year} standouts`);
    },
  });

  const remove = useMutation({
    mutationFn: (contentId) =>
      removeStandout({ contentId, year: currentStandoutYear() }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["standouts"] });
      queryClient.invalidateQueries({ queryKey: ["list"] });
      toast.success(`Removed from your ${data.year} standouts`);
    },
  });

  return {
    addStandout: safeMutate(add.mutateAsync),
    removeStandout: safeMutate(remove.mutateAsync),
    isAdding: add.isPending,
    isRemoving: remove.isPending,
  };
}
